// StudioPanel — the Studio's action panel (pen "Studio · Action panel";
// owner, 29 Oct: the right side is for action only): a switch "Chat |
// Needs you <count>" at the top, Chat chosen by default; Chat is the
// conversation, newest at the bottom; Needs you is the whole queue, each
// item with its one action; the one composer (StudioComposer.tsx) always at
// the foot, sending to the conversation Chat shows. Nothing waiting (or no answer
// yet): "Needs you" without a count. Pure: the view shown comes in, a pick
// goes out; the bodies are rendered by the host.

export type { ActionView };
export { StudioPanel };

import { exhaustSwitchCase } from "@bilda/utils";
import { useLingui } from "@lingui/react/macro";
import type { ReactNode } from "react";
import { Icon } from "../Icon/Icon.js";
import "./StudioPanel.css";

type ActionView = "chat" | "needs_you";

const VIEWS: readonly ActionView[] = ["chat", "needs_you"];

const StudioPanel = ({
    view,
    waiting,
    bodies,
    composer,
    onView,
}: {
    readonly view: ActionView;
    // How many things wait for the author (0 or unknown: no count).
    readonly waiting: number;
    readonly bodies: { readonly [K in ActionView]: ReactNode };
    // The panel's one composer, under both views.
    readonly composer: ReactNode;
    readonly onView: (view: ActionView) => void;
}): ReactNode => {
    const { t } = useLingui();
    const label = (of: ActionView): ReactNode => {
        switch (of) {
            case "chat":
                return (
                    <>
                        <Icon name="chat" size="xs" />
                        {t`Chat`}
                    </>
                );
            case "needs_you":
                return waiting > 0 ? (
                    <>
                        <span className="bilda-studio-panel__dot" />
                        {t`Needs you`}
                        <span className="bilda-studio-panel__count">
                            {waiting}
                        </span>
                    </>
                ) : (
                    t`Needs you`
                );
            default:
                return exhaustSwitchCase(of);
        }
    };
    return (
        <aside className="bilda-studio-panel" aria-label={t`Assistant`}>
            <div className="bilda-studio-panel__switch">
                <div
                    className="bilda-studio-panel__track"
                    role="tablist"
                    aria-label={t`Assistant`}
                >
                    {VIEWS.map((of) => (
                        <button
                            key={of}
                            type="button"
                            role="tab"
                            id={`bilda-studio-view-${of}`}
                            aria-selected={of === view}
                            aria-controls="bilda-studio-panel-body"
                            className="bilda-studio-panel__side"
                            onClick={() => onView(of)}
                        >
                            {label(of)}
                        </button>
                    ))}
                </div>
            </div>
            <div
                id="bilda-studio-panel-body"
                role="tabpanel"
                aria-labelledby={`bilda-studio-view-${view}`}
                className="bilda-studio-panel__body"
                data-view={view}
            >
                {bodies[view]}
            </div>
            {composer}
        </aside>
    );
};
